import type { Polygon } from "@genz/engine-bind";

/**
 * Fill the pixels whose CENTRE is inside a normalized polygon, row by row.
 *
 * AUD-2 IM-6's worst offender: `applyMatteToSource`'s protect-product loop called
 * `pointInPolyNorm` PER PIXEL — 4.19 M px × 4 edges = ~16.8 M non-inlined operations, 58 ms
 * measured at 2048 × 2048 on the shipped quad. This is the same predicate evaluated once per ROW.
 *
 * IDENTICAL, not approximately identical, and the reason is fragile enough to spell out: each
 * crossing is computed with the EXACT expression `pointInPolyNorm` uses, `|| 1e-9` guard included,
 * then sorted ascending and filled over the HALF-OPEN spans [c(2k), c(2k+1)) — which is precisely
 * the ray-cast toggle rule `x < c`. Do not factor the expression, precompute a reciprocal, or
 * round the crossings: `scanline.test.ts` compares the whole pixel set against
 * `pointInPolyNorm` and will go red.
 *
 * review-1 M-5 — the `|| 1e-9` guard is UNREACHABLE, here and in `pointInPolyNorm`. A horizontal
 * edge has `yi === yj`, so `(yi > py) !== (yj > py)` is false for every `py` and the division is
 * never taken; removing it changes no output, which `scanline.test.ts`'s "the `|| 1e-9` guard is
 * dead code" block proves against a guard-free copy of this kernel. It is kept anyway, and only
 * for this: character-identity with `pointInPolyNorm`'s expression is the whole argument above,
 * and an expression that merely LOOKS the same is worth less than one that is the same.
 */
export function forEachPixelInPolyNorm(
  width: number,
  height: number,
  poly: Polygon,
  visit: (index: number) => void,
): void {
  if (poly.length < 3 || width <= 0 || height <= 0) return;
  // Reused across rows: at most one crossing per edge.
  const crossings = new Float64Array(poly.length);
  for (let y = 0; y < height; y++) {
    const py = (y + 0.5) / height;
    let n = 0;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const xi = poly[i].x, yi = poly[i].y, xj = poly[j].x, yj = poly[j].y;
      // The SAME half-open edge test `pointInPolyNorm` uses, so an edge is counted for exactly the
      // rows it spans — this is what makes shared vertices behave identically.
      if ((yi > py) !== (yj > py)) {
        // `|| 1e-9` is dead code reached by no input (M-5, above): this branch already excludes
        // `yi === yj`. It stays so the expression is character-identical to `pointInPolyNorm`'s.
        crossings[n++] = ((xj - xi) * (py - yi)) / (yj - yi || 1e-9) + xi;
      }
    }
    if (n < 2) continue;
    // Insertion sort: `n` is the edge count (4 for the shipped quad), so this beats a comparator.
    for (let a = 1; a < n; a++) {
      const v = crossings[a];
      let b = a - 1;
      while (b >= 0 && crossings[b] > v) { crossings[b + 1] = crossings[b]; b--; }
      crossings[b + 1] = v;
    }
    const row = y * width;
    for (let k = 0; k + 1 < n; k += 2) {
      const lo = crossings[k], hi = crossings[k + 1];
      // `pointInPolyNorm` toggles on `px < c`, so a pixel is inside when it is at or past an ODD
      // number of crossings: the span is [lo, hi) in the same strict sense. `px = (x + 0.5) / width`
      // ⇒ x > lo * width - 0.5 and x < hi * width - 0.5. `Math.floor(v) + 1` is the first integer
      // STRICTLY greater than v when v is not an integer, and when it is an integer the pixel at
      // that x has centre exactly lo — which `px < c` excludes — so the same `+1` is right.
      let x0 = Math.floor(lo * width - 0.5) + 1;
      const xEndExclusive = Math.ceil(hi * width - 0.5);
      if (x0 < 0) x0 = 0;
      const x1 = xEndExclusive > width ? width : xEndExclusive;
      for (let x = x0; x < x1; x++) visit(row + x);
    }
  }
}
